<template>
  <dialog ref="dialogRef" class="positions-dialog" @close="emit('close')" @click="handleBackdropClick">
    <div class="positions-card">
      <div class="positions-title">{{ localize('handshake.positions.title') }}</div>

      <div class="positions">
        <div v-for="position in shown" :key="position" class="position-row" @click="toggle(position)">
          <button
            type="button"
            class="position-check"
            :class="{ 'is-saving': isSaving(position) }"
            :aria-label="localize('handshake.game.checkPosition')"
            :aria-pressed="isChecked(position)"
          >
            <GamIcon v-if="isChecked(position)" :name="GamIconName.CHECK" size="18px" class="check-icon" is-current />
            <span v-else class="check-empty" />
          </button>
          <HandshakePositionMark :position-index="position" align="start" show-number>
            <template #side>
              <div class="position-photos">
                <button
                  v-if="preview(position)"
                  type="button"
                  class="photo-thumb"
                  @click.stop="emit('openPhoto', preview(position)!.imageBlobHash, position)"
                >
                  <img :src="photoSrc(preview(position)!.imageBlobHash)" alt="" />
                </button>
                <button
                  v-if="canAddPhoto(position)"
                  type="button"
                  class="photo-add"
                  :aria-label="localize('handshake.photo.add')"
                  :disabled="uploadingPosition === position"
                  @click.stop="pickPhoto(position)"
                >
                  <GamIcon :name="GamIconName.IMAGE" size="16px" is-current />
                </button>
              </div>
            </template>
          </HandshakePositionMark>
        </div>
      </div>

      <div class="positions-close" @click="emit('close')">
        <GamIcon :name="GamIconName.X_CLOSE" size="24px" is-current />
      </div>
    </div>
  </dialog>
</template>

<script lang="ts" setup>
import { gambits, localize } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { useHandshakePhotoUpload } from '@/views/composables/hooks/useHandshakePhotoUpload';
import HandshakePositionMark from '@/views/main/handshakes/HandshakePositionMark.vue';
import { photosInSlot, photosToUpload } from '@/views/main/handshakes/utils/handshake-photos';
import { readAskedPositions } from '@/views/main/handshakes/utils/played-prompt';
import { useFileDialog } from '@vueuse/core';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useToast } from 'vue-toastification';

const props = defineProps<{
  handshakeId: string;
  /** The positions checked when the sheet opened. Unchecked ones stay here so they can be put back. */
  positions: number[];
  /** Play started these. They show unchecked until someone checks them. */
  startedPositions?: number[];
  /** Whose photo replaces the upload button. */
  userId?: string | null;
  photos?: { imageBlobHash: string; userId: string | null; positionIndex: number | null; createdAt?: string }[];
}>();

const emit = defineEmits<{ close: []; change: [positions: number[]]; openPhoto: [hash: string, position: number] }>();

const { upload } = useHandshakePhotoUpload();
const { open: openFileDialog, onChange: onFileChange } = useFileDialog({
  accept: '.jpg, .jpeg, .png',
  multiple: true,
});
const uploadingPosition = ref<number | null>(null);
let pendingPosition: number | null = null;

const toast = useToast();
const dialogRef = ref<HTMLDialogElement>();
/** Saved positions stay checked. Play, and a No, stay in the list unchecked. */
const shown = ref<number[]>([
  ...new Set([...props.positions, ...(props.startedPositions ?? []), ...readAskedPositions(props.handshakeId)]),
]);
const checked = ref(new Set(props.positions));
const saving = ref(new Set<number>());

onMounted(() => {
  dialogRef.value?.showModal();
});

onBeforeUnmount(() => {
  if (dialogRef.value?.open) dialogRef.value.close();
});

const handleBackdropClick = (event: MouseEvent) => {
  if (event.target === dialogRef.value) emit('close');
};

const isChecked = (position: number): boolean => checked.value.has(position);

const isSaving = (position: number): boolean => saving.value.has(position);

const photosFor = (position: number) => (props.photos ?? []).filter((photo) => photo.positionIndex === position);

/** One square: the newest photo on this position, whoever uploaded it. */
const preview = (position: number) => {
  const photos = photosFor(position);

  return [...photos].sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? ''))[0];
};

/** The square is one preview. The button stays beside it until this player has five here. */
const canAddPhoto = (position: number): boolean =>
  !!props.userId && isChecked(position) && photosInSlot({ photos: props.photos }, props.userId, position) < 5;

const photoSrc = (hash: string): string => gambits.imageService.getImageSrc(hash, 64);

const pickPhoto = (position: number) => {
  pendingPosition = position;
  openFileDialog();
};

onFileChange(async (files) => {
  const position = pendingPosition;
  pendingPosition = null;
  if (!files?.length || position == null || !canAddPhoto(position) || !props.userId) return;

  const already = photosInSlot({ photos: props.photos }, props.userId, position);
  const batch = photosToUpload(files, already);
  if (!batch.length || batch.length < files.length) toast.error(localize('handshake.photo.taken'));
  if (!batch.length) return;

  uploadingPosition.value = position;
  await upload(props.handshakeId, batch, position);
  uploadingPosition.value = null;
});

/** One save at a time, so a slower row cannot write an older list over a newer one. */
let saveQueue: Promise<void> = Promise.resolve();

const toggle = (position: number) => {
  if (saving.value.has(position)) return;

  const turningOff = checked.value.has(position);
  const next = new Set(checked.value);
  if (turningOff) next.delete(position);
  else next.add(position);
  checked.value = next;
  saving.value = new Set(saving.value).add(position);

  saveQueue = saveQueue
    .then(async () => {
      try {
        const result = turningOff
          ? await gambits.userService.uncheckHandshakePosition(props.handshakeId, position)
          : await gambits.userService.checkHandshakePosition(props.handshakeId, position);

        if (result.error || !result.data) {
          const reverted = new Set(checked.value);
          if (turningOff) reverted.add(position);
          else reverted.delete(position);
          checked.value = reverted;
          toast.error(localize('handshake.positions.failed'));
          return;
        }

        emit('change', result.data.positions);
      } finally {
        const still = new Set(saving.value);
        still.delete(position);
        saving.value = still;
      }
    })
    .catch(() => undefined);
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.positions-dialog {
  width: 100%;
  height: 100%;
  border: 0;
  padding: var(--sai-top) 1rem var(--sai-bottom);
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overflow: hidden;

  &::backdrop {
    background: rgba(23, 23, 23, 0.75);
  }
}

.positions-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: min(100%, 420px);
  max-height: min(70vh, 640px);
  padding: var(--spacing-xxl) var(--spacing-xl);
  padding-bottom: 6.5rem;
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.positions-title {
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  color: var(--color-white);
  margin-bottom: var(--spacing-xl);
}

.positions {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.position-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
}

.position-row :deep(.HandshakePositionMark) {
  flex: 1;
  min-width: 0;
  width: auto;
}

/* The check the recommendation uses: an empty rounded square, a pink tick once taken. */
.position-check {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 2rem;
  height: 2rem;
  margin-top: 4px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;

  &.is-saving::after {
    content: '';
    position: absolute;
    width: 26px;
    height: 26px;
    border: 2px solid var(--color-white-30);
    border-top-color: var(--color-white);
    border-radius: 50%;
    animation: check-spin 600ms linear infinite;
  }

  .check-icon {
    color: var(--color-pink);
  }

  .check-empty {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: 2px solid var(--color-white-20);
  }
}

.positions-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}

@keyframes check-spin {
  to {
    transform: rotate(360deg);
  }
}

.position-photos {
  display: flex;
  flex: none;
  align-items: center;
  gap: 4px;
  height: 44px;
}

.photo-thumb,
.photo-add {
  box-sizing: border-box;
  flex: none;
  width: 44px;
  height: 44px;
  padding: 0;
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
}

.photo-add {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: var(--color-white-8);
  color: var(--color-white-70);
}

.photo-thumb {
  border: 1px solid var(--color-white-100);
  border-radius: var(--radius-x-small);
  overflow: hidden;
  background: none;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>
